import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiMessageCircle, FiTarget, FiCalendar, FiBell, FiBarChart2, FiShield, FiCopy, FiCheck } from "react-icons/fi";
import SalesSandbox from "./sales/SalesSandbox";
import { PUBLIC_API_BASE } from "../chatbot/chat";
import { salesSamples } from "../chatbot/sales";

const FEATURES = [
  [FiMessageCircle, "Talks to visitors, any hour", "Greets visitors, answers questions about your products and prices from your own facts, by text or voice, in their language."],
  [FiTarget, "Qualifies and scores", "Finds out what they need, their budget and timeline, and your own questions - one at a time, like a good salesperson. Every lead gets a score with the reasons."],
  [FiCalendar, "Books the call", "Shows your free times in your time zone and books them. No double bookings, with a calendar file for the visitor and for you."],
  [FiBell, "Tells you at once", "Hot leads reach you by email and by a signed webhook to your CRM, Zapier or Make. Every lead keeps the page and campaign it came from."],
  [FiBarChart2, "Shows what works", "A lead inbox with notes, status and CSV export, and a funnel from visitors to deals won, by day and by page."],
  [FiShield, "Respects privacy", "Consent is asked and recorded, spam robots are caught, visitors' data can be erased on request, and the bot never asks for card numbers or passwords."],
];
const STEPS = ["The visitor chats, or the pop-up starts the chat", "The bot answers and asks one qualifying question at a time", "Contact details are saved as a lead with a score", "Your team is told, and the visitor books a call", "You follow up and mark the lead won"];
const TABS = [["leads", "Leads (API)"], ["meetings", "Meetings (API)"], ["webhook", "Webhook to your CRM"], ["embed", "Website chat window"]];

function Code({ text }) {
  const [done, setDone] = useState(false);
  return (
    <div className="relative">
      <pre className="rounded-xl bg-black/40 border border-navy-border p-4 pr-24 overflow-x-auto text-[12.5px] leading-relaxed text-gray-200 whitespace-pre" tabIndex={0}><code>{text}</code></pre>
      <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* select by hand */ } }}
        className="absolute top-2.5 right-2.5 px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-xs inline-flex items-center gap-1.5">{done ? <><FiCheck /> Copied</> : <><FiCopy /> Copy</>}</button>
    </div>
  );
}

// AI Sales and Lead Generation Bot: a try-it box (describe a business, chat as a visitor, watch the lead card fill and score), what the bot does, and the API, webhook and chat window for a client's developer.
export default function SalesBotTool() {
  const [tab, setTab] = useState("leads");
  const samples = useMemo(() => salesSamples(PUBLIC_API_BASE), []);
  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Sales and Lead Generation Bot</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-10">
        A sales assistant for your website that never sleeps. It talks to visitors, finds out what they need and what they can spend, saves them as scored leads, books calls with your team and tells you the moment a hot lead appears.
        Describe a business below and play the visitor.
      </p>

      <SalesSandbox />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14 mb-10">
        {FEATURES.map(([Icon, title, text]) => (
          <div key={title} className="rounded-2xl bg-white/5 border border-navy-border p-5">
            <span className="w-9 h-9 rounded-xl bg-brand-gradient flex items-center justify-center mb-3"><Icon /></span>
            <div className="font-semibold mb-1">{title}</div>
            <p className="text-sm text-gray-400">{text}</p>
          </div>
        ))}
      </div>

      <ol className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-14 text-sm">
        {STEPS.map((s, i) => <li key={s} className="rounded-xl bg-white/5 border border-navy-border p-4"><span className="text-xs text-accent-light font-bold">Step {i + 1}</span><div className="text-gray-300 mt-1">{s}</div></li>)}
      </ol>

      <h3 className="text-xl md:text-2xl font-bold mb-2">Connect it to your website and your CRM</h3>
      <p className="text-gray-400 mb-5 max-w-3xl">A chat window with one line of code, an API for your own app, and a signed webhook that pushes every lead to the tools you already use.</p>
      <div role="tablist" aria-label="Examples" className="flex flex-wrap gap-2 mb-3">
        {TABS.map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key} type="button" onClick={() => setTab(key)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border ${tab === key ? "bg-accent-light text-navy border-accent-light" : "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light"}`}>{label}</button>
        ))}
      </div>
      <Code text={samples[tab]} />
      <p className="text-xs text-gray-500 mt-2">Chat, voice and knowledge-base endpoints are described on the <Link className="underline" to="/ai-chatbots-and-virtual-assistants?tool=custom-ai-chatbot-development">Custom AI Chatbot</Link> page.</p>

      <div className="mt-12 rounded-2xl bg-white/5 border border-navy-border p-6 md:p-8 text-center">
        <h3 className="text-lg font-bold mb-2">Want a sales bot that brings you leads?</h3>
        <p className="text-sm text-gray-400 max-w-2xl mx-auto mb-5">Tell us what you sell and who your customers are. We set up the bot with your offer and questions, connect it to your email and CRM, and add it to your website.</p>
        <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-gradient font-semibold shadow-lg shadow-accent/20">Get your sales bot <FiArrowRight /></Link>
      </div>
    </div>
  );
}
